// HeaderBranchSelector.jsx - Enhanced branch selector with improved UI

import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { getBranchesThunk } from "../../store/thunks/branchThunks";
import { setSelectedBranch } from "../../store/thunks/rateThunks";
import {
  OfficeBuildingIcon,
  ChevronDownIcon,
  CheckIcon,
  SwitchHorizontalIcon,
  LocationMarkerIcon,
  SearchIcon,
  XIcon,
} from "@heroicons/react/outline";
import { Transition } from "@headlessui/react";

const HeaderBranchSelector = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [dropdownStyles, setDropdownStyles] = useState({});
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const { user, isRootAdmin } = useSelector((state) => state.auth);
  const { branches, loadingList } = useSelector((state) => state.branch);
  const { selectedBranch } = useSelector((state) => state.rate.ui);

  // Fetch branches on component mount
  useEffect(() => {
    dispatch(getBranchesThunk({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Calculate optimal dropdown position relative to viewport
  const calculateDropdownPosition = () => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const dropdownHeight = 420;
    const gap = 8;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const safeMargin = 16;

    // Responsive width
    const isMobile = viewportWidth < 640;
    const dropdownWidth = isMobile ? Math.min(viewportWidth - 32, 360) : 360;

    let styles = {
      position: "fixed",
      zIndex: 9999,
      width: `${dropdownWidth}px`,
    };

    // Determine vertical position
    if (
      buttonRect.bottom + gap + dropdownHeight + safeMargin <=
      viewportHeight
    ) {
      styles.top = `${buttonRect.bottom + gap}px`;
    } else if (buttonRect.top - gap - dropdownHeight - safeMargin >= 0) {
      styles.bottom = `${viewportHeight - buttonRect.top + gap}px`;
    } else {
      if (viewportHeight - buttonRect.bottom > buttonRect.top) {
        styles.top = `${buttonRect.bottom + gap}px`;
        styles.maxHeight = `${
          viewportHeight - buttonRect.bottom - gap - safeMargin
        }px`;
      } else {
        styles.bottom = `${viewportHeight - buttonRect.top + gap}px`;
        styles.maxHeight = `${buttonRect.top - gap - safeMargin}px`;
      }
    }

    // Determine horizontal position - center on mobile, align to button on desktop
    if (isMobile) {
      styles.left = `${(viewportWidth - dropdownWidth) / 2}px`;
    } else {
      const buttonCenter = buttonRect.left + buttonRect.width / 2;
      let leftPos = buttonCenter - dropdownWidth / 2;

      if (leftPos + dropdownWidth + safeMargin > viewportWidth) {
        leftPos = viewportWidth - dropdownWidth - safeMargin;
      }
      if (leftPos < safeMargin) {
        leftPos = safeMargin;
      }
      styles.left = `${leftPos}px`;
    }

    setDropdownStyles(styles);
  };

  // Calculate position when dropdown opens
  useEffect(() => {
    if (isOpen) {
      calculateDropdownPosition();

      const handleResize = () => calculateDropdownPosition();
      window.addEventListener("resize", handleResize);
      window.addEventListener("scroll", handleResize, true);

      return () => {
        window.removeEventListener("resize", handleResize);
        window.removeEventListener("scroll", handleResize, true);
      };
    }
  }, [isOpen]);

  // Get accessible branches for the user from user.branchAccess
  const accessibleBranches = useMemo(() => {
    if (!branches.length) return [];

    const userPrimaryBranch = user?.branchAccess?.primaryBranch;

    if (isRootAdmin) {
      return branches.map((branch) => ({
        ...branch,
        isPrimary: branch.code === userPrimaryBranch,
        accessLevel: "FULL_CONTROL",
      }));
    }

    const {
      primaryBranch,
      accessLevel,
      secondaryBranches = [],
    } = user?.branchAccess || {};

    if (!primaryBranch) return [];

    const result = [];

    const primaryBranchData = branches.find((b) => b.code === primaryBranch);
    if (primaryBranchData) {
      result.push({
        ...primaryBranchData,
        isPrimary: true,
        accessLevel: accessLevel || "MANAGE",
      });
    }

    secondaryBranches.forEach((sec) => {
      if (!sec.branchCode || typeof sec.branchCode !== "string") return;

      const branchData = branches.find((b) => b.code === sec.branchCode);
      if (branchData) {
        result.push({
          ...branchData,
          isPrimary: false,
          accessLevel: sec.accessLevel || "VIEW",
        });
      }
    });

    return result;
  }, [branches, user?.branchAccess, isRootAdmin]);

  const primaryBranch = useMemo(() => {
    return accessibleBranches.find((b) => b.isPrimary);
  }, [accessibleBranches]);

  const filteredBranches = useMemo(() => {
    if (!searchQuery) return accessibleBranches;

    const query = searchQuery.toLowerCase();
    return accessibleBranches.filter(
      (branch) =>
        branch.name?.toLowerCase().includes(query) ||
        branch.code?.toLowerCase().includes(query) ||
        branch.city?.toLowerCase().includes(query)
    );
  }, [accessibleBranches, searchQuery]);

  const currentBranch = useMemo(() => {
    return (
      accessibleBranches.find((b) => b.code === selectedBranch) ||
      primaryBranch ||
      accessibleBranches[0] ||
      null
    );
  }, [accessibleBranches, selectedBranch, primaryBranch]);

  useEffect(() => {
    if (accessibleBranches.length > 0) {
      const shouldAutoSelect =
        !selectedBranch ||
        !accessibleBranches.find((b) => b.code === selectedBranch);

      if (shouldAutoSelect) {
        const branchToSelect = primaryBranch || accessibleBranches[0];
        if (branchToSelect) {
          dispatch(setSelectedBranch(branchToSelect.code));
        }
      }
    }
  }, [selectedBranch, accessibleBranches, primaryBranch, dispatch]);

  const handleBranchChange = (branchCode) => {
    dispatch(setSelectedBranch(branchCode));
    setIsOpen(false);
    setSearchQuery("");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  // Access level badge component
  const AccessBadge = ({ level }) => {
    const config = {
      VIEW: {
        bg: "bg-blue-50",
        text: "text-blue-600",
        border: "border-blue-200",
        label: "View",
      },
      MANAGE: {
        bg: "bg-emerald-50",
        text: "text-emerald-600",
        border: "border-emerald-200",
        label: "Manage",
      },
      FULL_CONTROL: {
        bg: "bg-violet-50",
        text: "text-violet-600",
        border: "border-violet-200",
        label: "Full Access",
      },
    };

    const style = config[level] || config.VIEW;

    return (
      <span
        className={`
          inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold
          ${style.bg} ${style.text} border ${style.border}
        `}
      >
        {style.label}
      </span>
    );
  };

  // Loading state
  if (loadingList && branches.length === 0) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-white/10 rounded-xl">
        <div className="animate-pulse flex items-center space-x-2">
          <div className="w-5 h-5 bg-white/20 rounded" />
          <div className="space-y-1">
            <div className="h-2 w-12 bg-white/20 rounded" />
            <div className="h-3 w-20 bg-white/20 rounded" />
          </div>
        </div>
      </div>
    );
  }

  // No branch available
  if (!currentBranch) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-red-500/20 rounded-xl border border-red-400/30">
        <OfficeBuildingIcon className="w-5 h-5 text-red-200" />
        <span className="text-red-100 text-sm font-medium">
          No branch access
        </span>
      </div>
    );
  }

  // Dropdown content
  const dropdownContent = (
    <Transition
      show={isOpen && accessibleBranches.length > 1}
      enter="transition ease-out duration-200"
      enterFrom="opacity-0 scale-95 translate-y-1"
      enterTo="opacity-100 scale-100 translate-y-0"
      leave="transition ease-in duration-150"
      leaveFrom="opacity-100 scale-100 translate-y-0"
      leaveTo="opacity-0 scale-95 translate-y-1"
    >
      <div
        ref={dropdownRef}
        className="bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-100"
        style={dropdownStyles}
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-primary via-primary to-primary-dark p-4 text-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-base flex items-center gap-2">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <SwitchHorizontalIcon className="w-4 h-4" />
              </div>
              Switch Branch
            </h3>
            <span className="text-xs bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full font-medium">
              {filteredBranches.length} of {accessibleBranches.length}
            </span>
          </div>

          {/* Search */}
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search branches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className="
                w-full px-4 py-2.5 pl-10 pr-10
                bg-white/15 backdrop-blur-sm rounded-xl
                text-white placeholder-white/60
                text-sm font-medium
                border border-white/20
                focus:outline-none focus:ring-2 focus:ring-white/40 focus:bg-white/20
                transition-all duration-200
              "
            />
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            {searchQuery && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchQuery("");
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-white/20 rounded-full transition-colors"
              >
                <XIcon className="w-4 h-4 text-white/60" />
              </button>
            )}
          </div>
        </div>

        {/* Branch List */}
        <div
          className="overflow-y-auto overscroll-contain"
          style={{ maxHeight: "320px" }}
        >
          {filteredBranches.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <SearchIcon className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-gray-500 font-medium">No branches found</p>
              <p className="text-gray-400 text-sm mt-1">
                Try a different search term
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {filteredBranches.map((branch) => {
                const isSelected = branch.code === currentBranch?.code;

                return (
                  <button
                    key={branch.code}
                    onClick={() => handleBranchChange(branch.code)}
                    className={`
                      w-full text-left p-3 rounded-xl transition-all duration-200
                      group relative
                      ${
                        isSelected
                          ? "bg-primary/5 ring-2 ring-primary/20"
                          : "hover:bg-gray-50 active:bg-gray-100"
                      }
                    `}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div
                        className={`
                          flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center
                          transition-colors duration-200
                          ${
                            isSelected
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-500 group-hover:bg-gray-200"
                          }
                        `}
                      >
                        <OfficeBuildingIcon className="w-5 h-5" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`
                              font-semibold text-sm truncate
                              ${isSelected ? "text-primary" : "text-gray-900"}
                            `}
                          >
                            {branch.name || "Unnamed Branch"}
                          </span>
                          {branch.isPrimary && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md bg-amber-50 border border-amber-200">
                              <span className="text-amber-500 text-xs">★</span>
                              <span className="text-amber-700 text-[10px] font-semibold">
                                Primary
                              </span>
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <code className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded font-mono">
                            {branch.code}
                          </code>
                          {branch.city && (
                            <span className="inline-flex items-center text-xs text-gray-500">
                              <LocationMarkerIcon className="w-3 h-3 mr-0.5 text-gray-400" />
                              {branch.city}
                            </span>
                          )}
                        </div>

                        <div className="mt-2">
                          <AccessBadge level={branch.accessLevel} />
                        </div>
                      </div>

                      {/* Selected indicator */}
                      {isSelected && (
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                          <CheckIcon className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="border-t border-gray-100 px-4 py-2.5 bg-gray-50/50">
          <p className="text-[11px] text-gray-400 text-center">
            Press{" "}
            <kbd className="px-1.5 py-0.5 bg-gray-200 rounded text-gray-600 font-mono text-[10px]">
              ESC
            </kbd>{" "}
            to close
          </p>
        </div>
      </div>
    </Transition>
  );

  return (
    <>
      {/* Main Button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2.5 px-3 py-2
          bg-white/10 hover:bg-white/20 
          rounded-xl transition-all duration-200 
          group border border-white/10 hover:border-white/20
          ${isOpen ? "bg-white/20 border-white/20" : ""}
          ${
            accessibleBranches.length <= 1 ? "cursor-default" : "cursor-pointer"
          }
        `}
        disabled={accessibleBranches.length <= 1}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
          <OfficeBuildingIcon className="w-4 h-4 text-white" />
        </div>

        <div className="text-left min-w-0">
          <div className="text-[10px] text-white/60 font-medium uppercase tracking-wide flex items-center gap-1">
            Branch
            {currentBranch.isPrimary && (
              <span className="text-amber-300">★</span>
            )}
          </div>
          <div className="text-sm text-white font-semibold truncate max-w-[120px] sm:max-w-[160px]">
            {currentBranch.name}
          </div>
        </div>

        {accessibleBranches.length > 1 && (
          <ChevronDownIcon
            className={`
              w-4 h-4 text-white/70 transition-transform duration-200 flex-shrink-0
              ${isOpen ? "rotate-180" : ""}
            `}
          />
        )}
      </button>

      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[9998] sm:hidden"
          onClick={() => {
            setIsOpen(false);
            setSearchQuery("");
          }}
        />
      )}

      {/* Portal for dropdown */}
      {typeof document !== "undefined" &&
        createPortal(dropdownContent, document.body)}
    </>
  );
};

export default HeaderBranchSelector;
